/**
 * A parent Business Unit or Business Unit Group, drawn as a link only when the
 * caller can open it (f9.2).
 *
 * f6.1 drew every parent as a link. From f9.1 a DM sees their client and not
 * the unit above it, and a link to a guaranteed `404` is a worse answer than
 * a label. So the parent is a link exactly when it is a row of the caller's
 * OWN scoped collection -- the list the server already narrowed. That is the
 * server's answer, read back; the browser computes no rule and holds no id
 * the server did not send it. When the server did not send the level's
 * navigation key its collection is empty by construction, so it is not
 * fetched at all.
 *
 * Presentation only: typing the parent's address still reaches the API,
 * which answers `404` on its own authority.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useBusinessUnitGroups, useBusinessUnits } from '@/api/queries';
import { useSessionScope } from '@/session/SessionScopeContext';

function ParentLink({ to, linkable, children }: { to: string; linkable: boolean; children: ReactNode }) {
  if (!linkable) return <span>{children}</span>;
  return (
    <Link className="cell-link" to={to}>
      {children}
    </Link>
  );
}

export function GroupParentLink({ group }: { group: { id: string; name: string } }) {
  const { canSee } = useSessionScope();
  const listed = useBusinessUnitGroups({ enabled: canSee('business-unit-groups') });
  const linkable = (listed.data?.business_unit_groups ?? []).some((row) => row.id === group.id);
  return (
    <ParentLink to={`/business-unit-groups/${group.id}`} linkable={linkable}>
      {group.name}
    </ParentLink>
  );
}

export function UnitParentLink({ unit }: { unit: { id: string; name: string } }) {
  const { canSee } = useSessionScope();
  const listed = useBusinessUnits({ enabled: canSee('business-units') });
  const linkable = (listed.data?.business_units ?? []).some((row) => row.id === unit.id);
  return (
    <ParentLink to={`/business-units/${unit.id}`} linkable={linkable}>
      {unit.name}
    </ParentLink>
  );
}
